import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Plus, Trash2 } from 'lucide-react';
import sessionsApi from 'api/requests/sessions';
import Button from 'components/_UI/Button';
import Checkbox from 'components/_UI/Checkbox';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import TextArea from 'components/_UI/TextArea';
import { useEvent } from 'context/events/Event';
import { SESSION_KINDS, eventDays, eventHours, formatDay, speakersOf, ticketProducts } from 'helpers/eventSessions';
import { Column } from 'styled/alignment/Column';
import { StyledFieldset, StyledLabel } from 'styled/UI/StyledForm';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import SessionImage from './SessionImage';
import SpeakerPhoto from './SpeakerPhoto';

const MAX_SPEAKERS = 12;
// Pedido abaixo disso é recusado no pagamento (e o formulário de ingressos já exige o mesmo).
const MIN_PAID_PRICE = 5;

let rowSequence = 0;
const nextKey = () => `row-${++rowSequence}`;

function firstError(error) {
    const errors = error?.response?.data?.errors;

    if (errors) {
        return Object.values(errors).flat()[0];
    }

    return error?.response?.data?.message || 'Não foi possível salvar a sessão.';
}

function toSpeakerRows(session) {
    // Sessão antiga só tem o nome em texto: ele vira o primeiro palestrante.
    return speakersOf(session)
        .map(person => ({
            key: nextKey(),
            id: person.id,
            name: person.name,
            photoUrl: person.photo_url,
            previewUrl: null,
            file: null,
            removePhoto: false,
        }));
}

/**
 * Dias que o select oferece: os do evento. Uma sessão antiga em dia que o evento
 * não tem mais mantém o dia dela na lista, senão o select a trocaria sem avisar.
 */
function dayOptions(days, current) {
    const options = days.map(day => ({ value: day, outside: false }));

    if (current && !days.includes(current)) {
        options.push({ value: current, outside: true });
        options.sort((a, b) => a.value.localeCompare(b.value));
    }

    return options;
}

function dayLabel(day) {
    const { weekday, label, year } = formatDay(day);

    return `${weekday}, ${label}/${year}`;
}

/** Evento de um dia só já vem com o dia escolhido; nos demais vale o último dia usado, se for do evento. */
function initialDay({ edit, days, defaultDate }) {
    if (edit?.date) {
        return edit.date;
    }

    if (!days.length) {
        return defaultDate || '';
    }

    if (days.length === 1) {
        return days[0];
    }

    return days.includes(defaultDate) ? defaultDate : '';
}

export default function SessionForm({ edit = null, onClose, onSuccess, defaultDate }) {
    const { event } = useEvent();
    const tickets = ticketProducts(event);
    const days = eventDays(event);
    const options = dayOptions(days, edit?.date);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [productIds, setProductIds] = useState(edit?.product_ids || []);
    const [openAccess, setOpenAccess] = useState(!!edit?.open_access);
    const [speakers, setSpeakers] = useState(() => toSpeakerRows(edit));
    const [newTickets, setNewTickets] = useState([]);
    // A foto de capa só sobe depois do salvar; até lá fica a prévia.
    const [cover, setCover] = useState({ url: edit?.image_url || null, file: null, remove: false });

    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        formState: { errors, dirtyFields },
    } = useForm({
        defaultValues: {
            title: edit?.title || '',
            kind: edit?.kind || 'talk',
            kind_label: edit?.kind_label || '',
            date: initialDay({ edit, days, defaultDate }),
            start: edit?.start || '',
            end: edit?.end || '',
            location: edit?.location || '',
            description: edit?.description || '',
            capacity: edit?.capacity || '',
        },
    });

    const kind = watch('kind');
    const day = watch('date');

    // Escolhido o dia, início e fim vêm com o horário do evento naquele dia. Só na
    // criação (editar não mexe no horário da sessão) e só no que o produtor ainda não digitou.
    useEffect(() => {
        const hours = !edit && eventHours(event, day);

        if (!hours) {
            return;
        }

        if (!dirtyFields.start) {
            setValue('start', hours.start);
        }

        if (!dirtyFields.end) {
            setValue('end', hours.end);
        }
    }, [day]);
    // As vagas da sessão são o ponto de partida da quantidade dos ingressos criados aqui.
    const capacity = watch('capacity');
    const seats = capacity ? String(capacity) : '';

    /** Enquanto o produtor não mexe na quantidade, ela acompanha as vagas da sessão. */
    const quantityOf = item => (item.quantityEdited ? String(item.quantity) : seats);

    function toggleTicket(id) {
        setProductIds(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));
    }

    function addSpeaker() {
        setSpeakers(current => [
            ...current,
            { key: nextKey(), id: null, name: '', photoUrl: null, previewUrl: null, file: null, removePhoto: false },
        ]);
    }

    function updateSpeaker(key, changes) {
        setSpeakers(current => current.map(person => (person.key === key ? { ...person, ...changes } : person)));
    }

    function removeSpeaker(key) {
        setSpeakers(current => current.filter(person => person.key !== key));
    }

    function addNewTicket() {
        setNewTickets(current => [
            ...current,
            { key: nextKey(), name: '', price: '', priceFloat: null, quantity: '', quantityEdited: false },
        ]);
    }

    function updateNewTicket(key, changes) {
        setNewTickets(current => current.map(item => (item.key === key ? { ...item, ...changes } : item)));
    }

    function removeNewTicket(key) {
        setNewTickets(current => current.filter(item => item.key !== key));
    }

    /** Devolve o que está errado nos ingressos novos, ou '' quando está tudo certo. */
    function newTicketsProblem() {
        for (const item of newTickets) {
            if (!item.name.trim()) {
                return 'Dê um nome ao ingresso que será criado.';
            }

            if (item.priceFloat === null || item.priceFloat === undefined) {
                return `Informe o preço do ingresso "${item.name}" (use 0 para gratuito).`;
            }

            if (item.priceFloat > 0 && item.priceFloat < MIN_PAID_PRICE) {
                return `O preço mínimo de um ingresso pago é R$ 5,00. Para "${item.name}", use R$ 5,00 ou mais, ou 0 para gratuito.`;
            }

            if (!Number(quantityOf(item)) || Number(quantityOf(item)) < 1) {
                return `Informe quantos ingressos "${item.name}" serão vendidos.`;
            }
        }

        return '';
    }

    function createPayload(content, people) {
        const payload = {
            title: content.title,
            kind: content.kind,
            kind_label: content.kind === 'other' ? content.kind_label : null,
            date: content.date,
            start: content.start,
            end: content.end || null,
            location: content.location || null,
            description: content.description || null,
            capacity: content.capacity ? Number(content.capacity) : null,
            open_access: openAccess,
            ...(cover.remove && !cover.file ? { remove_image: true } : {}),
            product_ids: openAccess ? [] : productIds,
            speakers: people.map(person => ({
                ...(person.id ? { id: person.id } : {}),
                name: person.name.trim(),
                ...(person.removePhoto ? { remove_photo: true } : {}),
            })),
        };

        if (!openAccess && newTickets.length) {
            payload.new_tickets = newTickets.map(item => ({
                name: item.name.trim(),
                price: item.priceFloat,
                quantity: Number(quantityOf(item)),
            }));
        }

        return payload;
    }

    /** A foto sobe depois do salvar, porque o palestrante novo só ganha id na resposta. */
    async function uploadPhotos(people, saved) {
        const failed = [];

        for (let index = 0; index < people.length; index += 1) {
            const person = people[index];
            const target = saved?.speakers?.[index];

            if (!person.file || !target) {
                continue;
            }

            try {
                const formData = new FormData();
                formData.append('photo', person.file);
                await sessionsApi.uploadSpeakerPhoto(event.slug, saved.id, target.id, formData);
            } catch (err) {
                failed.push(person.name);
            }
        }

        return failed;
    }

    async function uploadCover(saved) {
        if (!cover.file || !saved?.id) {
            return true;
        }

        try {
            const formData = new FormData();
            formData.append('image', cover.file);
            await sessionsApi.uploadImage(event.slug, saved.id, formData);

            return true;
        } catch (err) {
            return false;
        }
    }

    async function submit(content) {
        const problem = openAccess ? '' : newTicketsProblem();

        if (problem) {
            setError(problem);
            return;
        }

        setLoading(true);
        setError('');

        const people = speakers.filter(person => person.name.trim());

        try {
            const payload = createPayload(content, people);
            const { data } = edit
                ? await sessionsApi.update(event.slug, edit.id, payload)
                : await sessionsApi.create(event.slug, payload);

            const failedPhotos = await uploadPhotos(people, data?.data);
            const coverSaved = await uploadCover(data?.data);

            // O formulário fecha aqui: mexer no estado depois disso é num componente já desmontado.
            onSuccess(edit ? 'editSession' : 'createSession', { failedPhotos, coverFailed: !coverSaved });
        } catch (err) {
            setError(firstError(err));
            setLoading(false);
        }
    }

    return (
        <Modal isOpen size='large' onClose={onClose}>
            <Text name='highlight'>{edit ? 'Editar' : 'Adicionar'} sessão</Text>

            <Form onSubmit={handleSubmit(submit)}>
                <FormBody>
                    <Fieldset
                        label='Título *'
                        placeholder='Ex.: IA no mercado de trabalho'
                        register={register}
                        name='title'
                        layout='squired'
                        validations={{ required: 'Informe o título da sessão', maxLength: { value: 255, message: 'Use até 255 caracteres' } }}
                        error={errors?.title?.message}
                    />

                    <Row $cols={2}>
                        <StyledFieldset>
                            <StyledLabel htmlFor='session-kind'>Tipo</StyledLabel>
                            <Select id='session-kind' {...register('kind')}>
                                {SESSION_KINDS.map(item => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </Select>
                        </StyledFieldset>
                        <Fieldset
                            label='Vagas (opcional)'
                            placeholder='Ex.: 30'
                            inputType='number'
                            register={register}
                            name='capacity'
                            layout='squired'
                            validations={{ min: { value: 1, message: 'Use 1 ou mais' } }}
                            error={errors?.capacity?.message}
                        />
                    </Row>

                    {kind === 'other' && (
                        <Fieldset
                            label='Qual tipo de sessão? *'
                            placeholder='Ex.: Oficina, Visita técnica, Roda de conversa'
                            register={register}
                            name='kind_label'
                            layout='squired'
                            validations={{
                                required: 'Escreva o tipo da sessão',
                                maxLength: { value: 60, message: 'Use até 60 caracteres' },
                            }}
                            error={errors?.kind_label?.message}
                        />
                    )}

                    <Row $cols={3}>
                        {options.length ? (
                            <StyledFieldset>
                                <StyledLabel htmlFor='session-date'>Dia *</StyledLabel>
                                <Select
                                    id='session-date'
                                    aria-invalid={errors?.date ? 'true' : undefined}
                                    {...register('date', { required: 'Escolha o dia da sessão' })}
                                >
                                    {options.length > 1 && <option value=''>Selecione o dia</option>}
                                    {options.map(item => (
                                        <option key={item.value} value={item.value}>
                                            {dayLabel(item.value)}{item.outside ? ' (fora das datas do evento)' : ''}
                                        </option>
                                    ))}
                                </Select>
                                {errors?.date && <FieldError role='alert'>{errors.date.message}</FieldError>}
                            </StyledFieldset>
                        ) : (
                            <Fieldset
                                label='Dia *'
                                inputType='date'
                                register={register}
                                name='date'
                                layout='squired'
                                validations={{ required: 'Informe o dia' }}
                                error={errors?.date?.message}
                            />
                        )}
                        <Fieldset
                            label='Início *'
                            inputType='time'
                            register={register}
                            name='start'
                            layout='squired'
                            validations={{ required: 'Informe o horário' }}
                            error={errors?.start?.message}
                        />
                        <Fieldset
                            label='Fim'
                            inputType='time'
                            register={register}
                            name='end'
                            layout='squired'
                        />
                    </Row>

                    <Fieldset
                        label='Local (sala, auditório)'
                        placeholder='Ex.: Auditório Principal'
                        register={register}
                        name='location'
                        layout='squired'
                    />

                    <Group>
                        <Label as='span'>Foto da sessão</Label>
                        <Hint>Opcional. Aparece no cartão da sessão na programação, acima do horário.</Hint>
                        <SessionImage
                            url={cover.remove ? null : (cover.previewUrl || cover.url)}
                            onPick={(file, previewUrl) => setCover(current => ({ ...current, file, previewUrl, remove: false }))}
                            onRemove={() => setCover(current => ({ ...current, file: null, previewUrl: null, remove: true }))}
                            onError={setError}
                        />
                    </Group>

                    <Group>
                        <Label as='span'>Palestrantes</Label>
                        <Hint>Adicione quem apresenta a sessão. A foto é opcional e aparece na programação.</Hint>

                        {speakers.map((person, index) => (
                            <SpeakerRow key={person.key}>
                                <SpeakerPhoto
                                    url={person.removePhoto ? null : (person.previewUrl || person.photoUrl)}
                                    name={person.name}
                                    onPick={(file, previewUrl) => updateSpeaker(person.key, { file, previewUrl, removePhoto: false })}
                                    onRemove={() => updateSpeaker(person.key, { file: null, previewUrl: null, removePhoto: true })}
                                    onError={setError}
                                />
                                <SpeakerName>
                                    <Fieldset
                                        placeholder={`Nome do palestrante ${index + 1}`}
                                        value={person.name}
                                        syncProp
                                        layout='squired'
                                        onChangeValue={value => updateSpeaker(person.key, { name: value })}
                                    />
                                </SpeakerName>
                                <IconButton type='button' aria-label='Remover palestrante' onClick={() => removeSpeaker(person.key)}>
                                    <Trash2 size={18} color={colors.danger} />
                                </IconButton>
                            </SpeakerRow>
                        ))}

                        {speakers.length < MAX_SPEAKERS && (
                            <AddLink type='button' onClick={addSpeaker}>
                                <Plus size={15} /> Adicionar palestrante
                            </AddLink>
                        )}
                    </Group>

                    <Field>
                        <Label>Descrição (opcional)</Label>
                        <Controller
                            name='description'
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextArea
                                    {...field}
                                    placeholder='Conte o que será abordado'
                                    register={register}
                                    name='description'
                                    squired
                                />
                            )}
                        />
                    </Field>

                    <Group>
                        <Label as='span'>Quem entra nesta sessão</Label>
                        <Hint>
                            Vincule ingressos que já existem ou crie um novo aqui mesmo. Quem compra o ingresso de uma sessão
                            precisa ter antes o ingresso do evento (o que não está ligado a nenhuma sessão).
                        </Hint>

                        <Checkbox
                            label='Qualquer ingresso do evento'
                            selected={openAccess}
                            onSelect={() => setOpenAccess(current => !current)}
                            marginTop='8px'
                        />

                        {!openAccess && (
                            <>
                                <SubLabel>Ingressos existentes</SubLabel>
                                {tickets.length ? tickets.map(ticket => (
                                    <Checkbox
                                        key={ticket.id}
                                        label={ticket.name}
                                        selected={productIds.includes(ticket.id)}
                                        onSelect={() => toggleTicket(ticket.id)}
                                        marginTop='0px'
                                    />
                                )) : (
                                    <Hint>Este evento ainda não tem ingressos.</Hint>
                                )}

                                <SubLabel>Criar ingresso para esta sessão</SubLabel>
                                {newTickets.map(item => (
                                    <NewTicket key={item.key}>
                                        <Fieldset
                                            label='Nome do ingresso'
                                            placeholder='Ex.: Minicurso de Git'
                                            value={item.name}
                                            syncProp
                                            layout='squired'
                                            onChangeValue={value => updateNewTicket(item.key, { name: value })}
                                        />
                                        <NewTicketRow>
                                            <CurrencyInputField
                                                label='Preço (R$ 0 = gratuito)'
                                                placeholder='R$ 0,00'
                                                layout='squired'
                                                value={item.price}
                                                onChange={(value, name, values) => updateNewTicket(item.key, { price: value ?? '', priceFloat: value ? values?.float ?? null : null })}
                                            />
                                            <Fieldset
                                                label='Quantidade'
                                                placeholder='Ex.: 30'
                                                inputType='number'
                                                value={quantityOf(item)}
                                                syncProp
                                                layout='squired'
                                                onChangeValue={value => updateNewTicket(item.key, { quantity: value, quantityEdited: true })}
                                            />
                                            <IconButton type='button' aria-label='Remover ingresso novo' onClick={() => removeNewTicket(item.key)}>
                                                <Trash2 size={18} color={colors.danger} />
                                            </IconButton>
                                        </NewTicketRow>
                                        {!item.quantityEdited && seats && (
                                            <QuantityHint>Quantidade igual às vagas da sessão ({seats}). Altere se este ingresso vender menos.</QuantityHint>
                                        )}
                                    </NewTicket>
                                ))}

                                <AddLink type='button' onClick={addNewTicket}>
                                    <Plus size={15} /> Criar ingresso aqui
                                </AddLink>
                                {newTickets.length > 0 && (
                                    <Hint>Os ingressos novos são criados ao salvar e já ficam vinculados a esta sessão.</Hint>
                                )}
                            </>
                        )}
                    </Group>
                </FormBody>

                {error && <ErrorText role='alert'>{error}</ErrorText>}

                {loading ? (
                    <Loader />
                ) : (
                    <Actions>
                        <Button variant='light' category='roundedBorded' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='featured' type='submit'>
                            {edit ? 'Atualizar' : 'Adicionar'}
                        </Button>
                    </Actions>
                )}
            </Form>
        </Modal>
    );
}

const Form = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

/* O modal centraliza o conteúdo na vertical; se o formulário passasse da
   altura dele, o topo ficaria cortado e sem rolagem. A rolagem fica só no
   corpo, e o botão de salvar continua à vista. */
const FormBody = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: calc(100dvh - 200px);
    overflow-y: auto;
    padding: 2px 4px 2px 2px;

    @media screen and (min-width: ${breakpoints.lg}) {
        max-height: calc(85vh - 190px);
    }
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;

    /* O campo de hora é uns 4px mais alto que os de texto (o relógio nativo dita a altura);
       sem isso a linha Dia / Início / Fim fica torta. */
    input[type='time'] {
        padding-top: 9px;
        padding-bottom: 9px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
        gap: 14px;
    }
`;

const Field = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 10px;
`;

const Label = styled.label`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 5px;
`;

const SubLabel = styled.span`
    margin-top: 14px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.04em;
`;

/* Mesmo desenho dos campos de texto ao lado (layout 'squired' do Fieldset): borda,
   altura e foco iguais. A seta é desenhada aqui porque a nativa fica colada na borda. */
const Select = styled.select`
    width: 100%;
    margin-top: 6px;
    padding: 11px 42px 11px 14px;
    border-radius: 8px;
    border: 1.5px solid #D4D4D4;
    background-color: ${colors.neutralLight};
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 16px 16px;
    -webkit-appearance: none;
    appearance: none;
    font-family: inherit;
    font-size: ${fonts.sizes.default};
    color: ${colors.nightDark};
    cursor: pointer;
    outline: transparent;
    transition: all ease .25s;

    &:hover { border-color: ${colors.nightLight}; }

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
        box-shadow: 0 0 0 3px ${panelAccentAlpha(colors.occasionalPurple, '1A')};
    }
`;

const Group = styled(Column)`
    width: 100%;
    align-items: flex-start;
    margin-top: 14px;
    padding: 12px 14px;
    border-radius: 10px;
    background: ${colors.neutral};
`;

const Hint = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
    line-height: 1.4;
    text-align: left;
`;

const QuantityHint = styled(Hint)`
    display: block;
    margin-top: 8px;
`;

const SpeakerRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
`;

const SpeakerName = styled.div`
    flex: 1;
    min-width: 0;
`;

const NewTicket = styled.div`
    width: 100%;
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.occasionalGrey};
`;

const NewTicketRow = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    align-items: end;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
        gap: 14px;
    }
`;

const IconButton = styled.button`
    flex: none;
    background: none;
    border: 0;
    padding: 8px;
    border-radius: 8px;
    cursor: pointer;

    &:hover { background: ${colors.neutralDark}; }
`;

const AddLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 0;
    background: none;
    border: 0;
    cursor: pointer;
    font-family: inherit;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${colors.occasionalPurple};
`;

const FieldError = styled.p`
    margin: 6px 0 0;
    font-size: 12px;
    line-height: 1.35;
    color: ${colors.danger};
`;

const ErrorText = styled.p`
    margin: 8px 0 0;
    font-size: ${fonts.sizes.small};
    color: ${colors.danger};
`;

const Actions = styled(Column)`
    margin-top: 15px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 5px;
    }
`;
